import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Store, MapPin, BadgeCheck, Crown, Share2, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import StarRating from "@/components/StarRating";
import ListingCard from "@/components/ListingCard";
import { track } from "@/lib/analytics";

const ORIGIN = "https://www.agroaluga.com.br";

function mapListing(row: any) {
  return {
    id: row.id, title: row.title, category: row.category, description: row.description || "",
    price: row.price, priceUnit: row.price_unit, city: row.city, state: row.state,
    rating: row.rating || 0, reviewCount: 0, images: row.images || [], availability: row.availability || [],
    phone: row.phone || "", whatsapp: row.whatsapp || "", email: row.email || "",
    ownerId: row.owner_id, ownerName: row.owner_name, featured: row.featured || false, status: row.status,
    views: row.views || 0, contactsRevealed: row.contacts_revealed || 0, createdAt: row.created_at,
    listingType: row.listing_type, latitude: row.latitude, neighborhood: row.neighborhood, longitude: row.longitude,
  };
}

const PLAN_LABEL: Record<string, string> = { pro: "Pro", ultra: "Ultra", free: "" };

export default function StorePage() {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState<any>(null);
  const [ads, setAds] = useState<any[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      if (!code) { setLoading(false); return; }
      const { data: storeData } = await supabase.rpc("store_public", { p_code: code });
      if (!active) return;
      if (!storeData?.found) { setStore(null); setLoading(false); return; }
      setStore(storeData);
      track("loja_visualizada", { type: "page_view", path: "/loja/" + code });
      const { data: rows } = await supabase.from("listings").select("*")
        .eq("owner_id", storeData.owner_id).eq("status", "active")
        .order("bumped_at", { ascending: false }).order("created_at", { ascending: false });
      if (!active) return;
      setAds((rows || []).map(mapListing));
      setLoading(false);
    })();
    return () => { active = false; };
  }, [code]);

  async function compartilhar() {
    const url = ORIGIN + "/loja/" + code;
    track("loja_compartilhada_publico", { type: "click" });
    if (navigator.share) { try { await navigator.share({ title: store?.name || "AgroAluga", text: "Confira a loja " + store?.name + " no AgroAluga", url }); } catch {} }
    else { try { await navigator.clipboard.writeText(url); } catch {} }
  }

  if (loading) return (<div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>);

  if (!store) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 px-6 text-center">
      <Store className="h-10 w-10 text-muted-foreground" />
      <h1 className="text-xl font-bold">Loja não encontrada</h1>
      <p className="text-muted-foreground">Esta loja não existe ou não está mais disponível.</p>
      <Link to="/" className="mt-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold">Ir para o início</Link>
    </div>
  );

  const planLabel = PLAN_LABEL[store.plan_id] || "";

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="bg-card border border-border rounded-2xl p-6 mb-8">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
          <div className="h-24 w-24 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center shrink-0">
            {store.avatar_url ? <img src={store.avatar_url} alt={store.name} className="h-full w-full object-cover" /> : <Store className="h-10 w-10 text-muted-foreground" />}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
              <h1 className="text-2xl font-heading font-bold">{store.name}</h1>
              <BadgeCheck className="h-5 w-5 text-primary" />
              {planLabel && <span className="inline-flex items-center gap-1 text-xs font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full"><Crown className="h-3 w-3" /> {planLabel}</span>}
            </div>
            {(store.city || store.state) && <p className="text-sm text-muted-foreground mt-1 flex items-center justify-center sm:justify-start gap-1"><MapPin className="h-4 w-4" /> {store.city}{store.state ? ", " + store.state : ""}</p>}
            {store.bio && <p className="text-sm text-foreground mt-2">{store.bio}</p>}
            <div className="flex items-center justify-center sm:justify-start gap-3 mt-3">
              {store.avg_rating != null ? (
                <div className="flex items-center gap-2">
                  <StarRating rating={Math.round(Number(store.avg_rating))} size={18} />
                  <span className="text-sm font-semibold">{Number(store.avg_rating).toFixed(1)}</span>
                  <span className="text-xs text-muted-foreground">({store.total_reviews} avaliações)</span>
                </div>
              ) : <span className="text-xs text-muted-foreground">Ainda sem avaliações</span>}
            </div>
          </div>
          <button onClick={compartilhar} className="flex items-center gap-2 text-sm font-semibold bg-primary text-primary-foreground rounded-md px-4 py-2 hover:bg-primary/90 transition-colors shrink-0"><Share2 className="h-4 w-4" /> Compartilhar</button>
        </div>
      </div>
      <h2 className="text-lg font-heading font-bold mb-4">Anúncios {ads.length > 0 && <span className="text-muted-foreground font-normal">({ads.length})</span>}</h2>
      {ads.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">Esta loja ainda não tem anúncios ativos.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{ads.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
      )}
    </div>
  );
}
